import ConfirmDialog from "@components/ConfirmDialog";
import Label from "@components/Label";
import MenuAutocompleteV2 from "@components/denseForm/MenuAutocompleteV2";
import { CustomSupplierListItem } from "@components/denseForm/customListItem/customSupplier";
import { Option } from "@components/hook-form/RHFAutocomplete";
import {
  Box,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import useSupplier from "@pages/ManualDocumentV2/hooks/useSupplier";
import { getManualDocumentCountryValue } from "@pages/ManualDocumentV2/utils/country";
import { createVendorCompanyIdOptions } from "@pages/ManualDocumentV2/utils/createVendorCompanyIdOptions";
import { Pencil, Trash } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  resolveSectionOverrides,
  sortSectionFields,
} from "../../core/sections";
import type { IFormEngine } from "../../core/types";
import { useCommonStyles } from "../../hooks/useCommonStyles";
import { EditVendorInformation } from "./EditVendorInformation";

interface IVendorInformationProps {
  translate: (text: any, options?: any) => string;
  engine: IFormEngine;
}

export const VendorInformation = ({
  translate,
  engine,
}: IVendorInformationProps) => {
  const {
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<any>();
  const { inputContainer } = useCommonStyles();
  const [isVendorDialogVisible, setIsVendorDialogVisible] = useState(false);
  const [hasOpenedEdit, setHasOpenedEdit] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [tooltipEnabled, setTooltipEnabled] = useState(false);
  const [vendorCompanyIDOptions, setVendorCompanyIDOptions] = useState<
    Option[]
  >([]);
  const { isFormReset } = engine;

  const isFrance = engine.config.complianceStandardKey === "FrancePA";

  const handleShowTooltip = ({ currentTarget }: React.MouseEvent<Element>) => {
    if (currentTarget.scrollWidth > currentTarget.clientWidth) {
      setTooltipEnabled(true);
    }
  };
  const hideTooltip = () => setTooltipEnabled(false);

  const { hiddenFields, extraFields, missingChips, hidden } = useMemo(() => {
    const ov = resolveSectionOverrides(engine.config, "vendor");
    return {
      hiddenFields: ov.hiddenFields ?? [],
      extraFields: sortSectionFields(ov.extraFields ?? []),
      missingChips: ov.missingChips ?? [],
      hidden: new Set(ov.hiddenFields ?? []),
    };
  }, [engine.config]);
  const isVisible = (key: string) => !hidden.has(key);

  const watched = watch();

  const {
    vendorCompanyID,
    vendorCompanyName,
    vendorEmail,
    vendorCountry,
    vendorCompanyAddress,
    logoURL,
    currency,
    vendorCompanyStreet,
    vendorCompanyStreetNumber,
    vendorCompanyLocality,
    vendorCompanyZipCode,
    vendorCompanyRegion,
    _vendorCompanyIDSource: vendorCompanyIDSource,
  } = watched;

  const companyID = watch("companyID");
  const vendorPeppolInboxCountry = watch("vendorPeppolInboxCountry");
  const vendorPeppolParticipantId = watch("vendorPeppolParticipantId");
  const vendorPeppolSchemeId = watch("vendorPeppolSchemeId");

  const isAccountPayable = engine.config.accountClass === "AccountPayable";

  const { selectOption, setSelectOption, ...supplierListProps } = useSupplier({
    label: vendorCompanyName ?? "",
    value: vendorCompanyName ?? "",
  });

  const VENDOR_ERROR_FIELDS = [
    "vendorCompanyAddress",
    "vendorEmail",
    "vendorCountry",
    isAccountPayable ? "currency" : "",
  ].filter(Boolean) as string[];
  const errorChips = Object.entries(errors ?? {})
    ?.filter(([key]) => VENDOR_ERROR_FIELDS.includes(key) && isVisible(key))
    .map(([field, error]) => ({
      field,
      message: (error as any)?.message || translate(field),
    }));

  useEffect(() => {
    createVendorCompanyIdOptions(
      vendorCompanyIDSource ?? "",
      setVendorCompanyIDOptions,
    );
  }, [vendorCompanyIDSource]);

  useEffect(() => {
    if (isVendorDialogVisible) setHasOpenedEdit(true);
  }, [isVendorDialogVisible]);

  const emptyMissingChips = missingChips.filter((c) => {
    const v = watched[c.name];
    return v === "" || v === undefined || v === null;
  });

  const optionalAddressKeys = [
    "vendorCompanyStreet",
    "vendorCompanyStreetNumber",
    "vendorCompanyLocality",
    "vendorCompanyZipCode",
    "vendorCompanyRegion",
  ] as const;

  const optionalValues = {
    vendorCompanyStreet,
    vendorCompanyStreetNumber,
    vendorCompanyLocality,
    vendorCompanyZipCode,
    vendorCompanyRegion,
  } as Record<string, any>;

  const visibleOptionalAddress = optionalAddressKeys.filter(isVisible);
  const showMissingAddressChips = visibleOptionalAddress.some(
    (k) =>
      optionalValues[k] === "" ||
      optionalValues[k] === undefined ||
      optionalValues[k] === null,
  );

  const showMissingVendorNameChip =
    isVisible("vendorCompanyName") &&
    !vendorCompanyName &&
    !errorChips.some((e) => e.field === "vendorCompanyName");
  const showMissingIDChip =
    isVisible("vendorCompanyID") &&
    !vendorCompanyID &&
    !errorChips.some((e) => e.field === "vendorCompanyID");
  const showMissingCountryChip =
    isVisible("vendorCountry") &&
    !vendorCountry &&
    !errorChips.some((e) => e.field === "vendorCountry");
  const showMissingCurrencyChip =
    isAccountPayable &&
    isVisible("currency") &&
    !currency &&
    !errorChips.some((e) => e.field === "currency");

  const VENDOR_FIELDS = [
    "vendorCompanyName",
    "vendorCompanyID",
    "vendorEmail",
    "vendorCompanyAddress",
    "vendorCompanyStreet",
    "vendorCompanyStreetNumber",
    "vendorCompanyLocality",
    "vendorCompanyZipCode",
    "vendorCompanyRegion",
    "vendorCountry",
    "currency",
  ];

  const clearVendorFields = () => {
    VENDOR_FIELDS.forEach((field) => setValue(field, ""));
    if (isFrance) {
      setValue("companyID", "");
      setValue("vendorPeppolInboxCountry", "");
      setValue("vendorPeppolParticipantId", "");
      setValue("vendorPeppolSchemeId", "");
      setValue("_peppolSelector", null);
    }
    clearErrors(VENDOR_FIELDS);
    setSelectOption({ label: "", value: "" });
    setHasOpenedEdit(false);
  };

  const handleConfirmDelete = () => {
    clearVendorFields();
    setIsConfirmOpen(false);
  };

  useEffect(() => {
    if (isFormReset > 0 && isAccountPayable) {
      clearVendorFields();
    }
  }, [isFormReset]);

  if (
    isAccountPayable &&
    !vendorCompanyName &&
    !isVendorDialogVisible &&
    !hasOpenedEdit
  ) {
    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="h6">
            {translate("vendor_information")}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <MenuAutocompleteV2
            optionTitle={translate("Vendor")}
            getOptionLabel={(option: any) => option?.label ?? ""}
            error={errors.vendorCompanyName?.message as string | undefined}
            onInputChange={(event) => {
              setSelectOption({
                label: event?.target.value,
                value: event?.target.value,
              });
              setValue("vendorCompanyName", event?.target.value ?? "");
              clearErrors("vendorCompanyName");
            }}
            onChange={(_event, newValue: any) => {
              setSelectOption({ label: newValue?.name, value: newValue?.name });
              setValue("vendorCompanyName", newValue?.name ?? "");
              setValue("vendorCompanyID", newValue?.registrationNumber ?? "");
              setValue("vendorEmail", newValue?.email ?? "");
              setValue("vendorCompanyAddress", newValue?.address ?? "");
              setValue("currency", newValue?.currency ?? "");
              setValue("vendorCompanyStreet", newValue?.street ?? "");
              setValue(
                "vendorCompanyStreetNumber",
                newValue?.streetNumber ?? "",
              );
              setValue("vendorCompanyLocality", newValue?.locality ?? "");
              setValue("vendorCompanyZipCode", newValue?.zipCode ?? "");
              setValue("vendorCompanyRegion", newValue?.region ?? "");
              setValue(
                "vendorCountry",
                getManualDocumentCountryValue(newValue?.country),
              );
              clearErrors(VENDOR_FIELDS);
            }}
            renderOption={(props, option) => (
              <CustomSupplierListItem {...props} option={option} />
            )}
            renderOptionHeader={() => <></>}
            {...supplierListProps}
            value={selectOption}
            onSearch={supplierListProps.getAsyncOptions}
            enableSearch={true}
            label={translate("companyName")}
          />
        </Grid>
      </Grid>
    );
  }

  const matchedPeppol = engine.peppolOptions?.find(
    (opt: any) => opt.peppolParticipantId === vendorPeppolParticipantId,
  );
  const displayCompanyId = companyID || matchedPeppol?.companyId;

  const peppolText = `Company ID (${displayCompanyId || "N/A"}), Scheme ID (${
    vendorPeppolSchemeId || "N/A"
  }), Peppol Participant ID (${vendorPeppolParticipantId || "N/A"}), Country (${
    vendorPeppolInboxCountry || "N/A"
  })`;

  return (
    <>
      <ConfirmDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title={translate("delete_vendor_title")}
        content={translate("delete_vendor_warning")}
        onConfirm={handleConfirmDelete}
        confirmLoading={false}
        isManualFormComponent={true}
        confirmText="remove"
      />
      <Grid container spacing={1}>
        {isVendorDialogVisible && (
          <EditVendorInformation
            open={isVendorDialogVisible}
            onClose={() => setIsVendorDialogVisible(false)}
            translate={translate}
            engine={engine}
            vendorCompanyIDOptions={vendorCompanyIDOptions}
            hiddenFields={hiddenFields}
            extraFields={extraFields}
          />
        )}
        <Grid item xs={12}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6">
              {translate("vendor_information")}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Box sx={inputContainer}>
            <Grid container alignItems="center">
              <Grid item xs>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Tooltip
                      title={vendorCompanyName}
                      onMouseEnter={handleShowTooltip}
                      onMouseLeave={hideTooltip}
                      disableHoverListener={!tooltipEnabled}
                      placement="top"
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={600}
                        noWrap
                        sx={{
                          fontSize: "12px",
                          textOverflow: "ellipsis",
                          maxWidth: 195,
                          cursor: "pointer",
                        }}
                      >
                        {vendorCompanyName}
                      </Typography>
                    </Tooltip>

                    {(errorChips.length > 0 ||
                      showMissingVendorNameChip ||
                      showMissingIDChip ||
                      showMissingCountryChip ||
                      showMissingCurrencyChip ||
                      showMissingAddressChips ||
                      emptyMissingChips.length > 0) && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.75,
                          rowGap: 0.5,
                          alignItems: "center",
                        }}
                      >
                        {showMissingVendorNameChip && (
                          <Label variant="ghost" color="error">
                            {translate("please_enter_company_name")}
                          </Label>
                        )}
                        {showMissingIDChip && (
                          <Label variant="ghost" color="error">
                            {translate("please_select_company_id")}
                          </Label>
                        )}
                        {showMissingCountryChip && (
                          <Label variant="ghost" color="error">
                            {translate("please_select_country")}
                          </Label>
                        )}
                        {showMissingCurrencyChip && (
                          <Label variant="ghost" color="error">
                            {translate("please_select_currency")}
                          </Label>
                        )}
                        {errorChips.map((error) => (
                          <Label
                            key={error.field}
                            variant="ghost"
                            color="error"
                          >
                            {translate(error.message)}
                          </Label>
                        ))}
                      </Box>
                    )}
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    {isVisible("vendorCompanyID") && vendorCompanyID && (
                      <Tooltip
                        title={vendorCompanyID}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={hideTooltip}
                        disableHoverListener={!tooltipEnabled}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            maxWidth: 195,
                            cursor: "pointer",
                          }}
                        >
                          {vendorCompanyID}
                        </Typography>
                      </Tooltip>
                    )}
                    {isVisible("vendorEmail") && vendorEmail && (
                      <Tooltip
                        title={vendorEmail}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={hideTooltip}
                        disableHoverListener={!tooltipEnabled}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            maxWidth: 195,
                            cursor: "pointer",
                          }}
                        >
                          • {vendorEmail}
                        </Typography>
                      </Tooltip>
                    )}
                    {isVisible("vendorCountry") && vendorCountry && (
                      <Tooltip
                        title={vendorCountry}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={hideTooltip}
                        disableHoverListener={!tooltipEnabled}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            maxWidth: 195,
                            cursor: "pointer",
                          }}
                        >
                          • {vendorCountry}
                        </Typography>
                      </Tooltip>
                    )}
                    {isAccountPayable && currency && (
                      <Tooltip
                        title={currency}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={hideTooltip}
                        disableHoverListener={!tooltipEnabled}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            maxWidth: 195,
                            cursor: "pointer",
                          }}
                        >
                          • {currency}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>

                  {vendorCompanyAddress && (
                    <Tooltip
                      title={vendorCompanyAddress}
                      onMouseEnter={handleShowTooltip}
                      onMouseLeave={hideTooltip}
                      disableHoverListener={!tooltipEnabled}
                      placement="top"
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          fontSize: "12px",
                          textOverflow: "ellipsis",
                          maxWidth: 195,
                          cursor: "pointer",
                        }}
                      >
                        {vendorCompanyAddress}
                      </Typography>
                    </Tooltip>
                  )}

                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    sx={{ alignItems: "center" }}
                  >
                    {isVisible("vendorCompanyStreet") && vendorCompanyStreet && (
                      <Tooltip
                        title={vendorCompanyStreet}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={hideTooltip}
                        disableHoverListener={!tooltipEnabled}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            maxWidth: 150,
                            cursor: "pointer",
                          }}
                        >
                          {vendorCompanyStreet}
                        </Typography>
                      </Tooltip>
                    )}
                    {isVisible("vendorCompanyStreetNumber") &&
                      vendorCompanyStreetNumber && (
                        <Tooltip
                          title={vendorCompanyStreetNumber}
                          onMouseEnter={handleShowTooltip}
                          onMouseLeave={hideTooltip}
                          disableHoverListener={!tooltipEnabled}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontSize: "12px",
                              textOverflow: "ellipsis",
                              maxWidth: 100,
                              cursor: "pointer",
                            }}
                          >
                            • {vendorCompanyStreetNumber}
                          </Typography>
                        </Tooltip>
                      )}
                    {isVisible("vendorCompanyLocality") &&
                      vendorCompanyLocality && (
                        <Tooltip
                          title={vendorCompanyLocality}
                          onMouseEnter={handleShowTooltip}
                          onMouseLeave={hideTooltip}
                          disableHoverListener={!tooltipEnabled}
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              fontSize: "12px",
                              textOverflow: "ellipsis",
                              maxWidth: 150,
                              cursor: "pointer",
                            }}
                          >
                            • {vendorCompanyLocality}
                          </Typography>
                        </Tooltip>
                      )}
                    {isVisible("vendorCompanyRegion") && vendorCompanyRegion && (
                      <Tooltip
                        title={vendorCompanyRegion}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={hideTooltip}
                        disableHoverListener={!tooltipEnabled}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            maxWidth: 100,
                            cursor: "pointer",
                          }}
                        >
                          • {vendorCompanyRegion}
                        </Typography>
                      </Tooltip>
                    )}
                    {isVisible("vendorCompanyZipCode") && vendorCompanyZipCode && (
                      <Tooltip
                        title={vendorCompanyZipCode}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={hideTooltip}
                        disableHoverListener={!tooltipEnabled}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            maxWidth: 100,
                            cursor: "pointer",
                          }}
                        >
                          • {vendorCompanyZipCode}
                        </Typography>
                      </Tooltip>
                    )}
                  </Stack>

                  {/* Peppol data */}
                  {isFrance &&
                    (displayCompanyId ||
                      vendorPeppolInboxCountry ||
                      vendorPeppolSchemeId ||
                      vendorPeppolParticipantId) && (
                      <Tooltip
                        title={peppolText}
                        onMouseEnter={handleShowTooltip}
                        onMouseLeave={hideTooltip}
                        disableHoverListener={!tooltipEnabled}
                        placement="top"
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            maxWidth: "100%",
                            cursor: "pointer",
                          }}
                        >
                          {peppolText}
                        </Typography>
                      </Tooltip>
                    )}
                </Stack>
              </Grid>
              <Grid item>
                <IconButton onClick={() => setIsVendorDialogVisible(true)}>
                  <Pencil strokeWidth="1.5px" size="18px" />
                </IconButton>
                {isAccountPayable && (
                  <IconButton onClick={() => setIsConfirmOpen(true)}>
                    <Trash strokeWidth="1.5px" size="18px" />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
